import {
  NotificationResource,
  User,
  UserFilters,
  UserNotificationPreferences,
} from '@automata/api/apiSchemas';
import { IRemoteNotification } from '@magicbell/magicbell-react';

import {
  NotificationCategory,
  NotificationCategoryEnum,
} from 'components/Notifications/types';

export const getNotificationCategory = (
  notification: IRemoteNotification
): NotificationCategory => {
  if (
    notification.category &&
    notification.category.toUpperCase() in NotificationCategoryEnum
  ) {
    return notification.category as NotificationCategory;
  }

  return 'neutral';
};

export const getCheckedStatus = (
  resources: NotificationResource | undefined,
  deviceID: string
): boolean => {
  if (!resources) return false;
  const { devices } = resources;
  if (devices && devices.length > 0) {
    return devices.includes(deviceID);
  }
  return false;
};

export const getUpdatedPreferenceCategories = (
  preferences: UserNotificationPreferences,
  checked: boolean,
  categorySlug: string,
  fromUsers?: UserFilters,
  channel?: 'in_app' | 'email'
): UserNotificationPreferences => {
  const currentPreference = preferences?.categories.find(
    (category) => category.slug === categorySlug
  );
  const channelIndex = currentPreference?.channels.findIndex(
    (el) => el.slug === channel
  );

  const updatedCategories: UserNotificationPreferences['categories'] =
    preferences.categories.map((category) => {
      if (category.slug === categorySlug) {
        if (fromUsers) {
          category.user_filters = fromUsers;
        }

        const categoryChannel = category.channels[channelIndex as number];
        if (categoryChannel) {
          categoryChannel.enabled = checked;
        }
      }
      return category;
    });

  return {
    ...preferences,
    categories: updatedCategories,
  };
};

export const formatCategory = (categorySlug: string): string => {
  return categorySlug.replaceAll('_', ' ');
};

export const prepareCategoryUsersValue = (
  allUsers: User[],
  userFilters?: UserFilters
) => {
  if (!userFilters) {
    return [];
  }

  let values = [];

  if (userFilters.all_users) {
    values.push('All users');
  }

  if (userFilters.automata_users) {
    values.push('Automata');
  }

  if (userFilters.user_ids && userFilters.user_ids.length > 0) {
    const filteredUsers = allUsers.filter((user) =>
      userFilters.user_ids.includes(user.user_id)
    );
    const names = filteredUsers.map((user) => user.name);
    values.push(names);
  }

  return values;
};

export const prepareUserFiltersValue = (
  value: string,
  fromUsers: UserFilters,
  allUsers: User[]
): UserFilters => {
  if (value === 'all_users') {
    return {
      all_users: !fromUsers.all_users,
      automata_users: false,
      user_ids: [],
    };
  }

  if (value === 'automata') {
    if (fromUsers.all_users) {
      return {
        all_users: false,
        automata_users: false,
        user_ids: allUsers.map((user) => user.user_id),
      };
    }
    return {
      all_users: fromUsers.all_users,
      automata_users: !fromUsers.automata_users,
      user_ids: fromUsers.user_ids,
    };
  }

  // If all users were selected, deselect selected user and keep others selected
  if (fromUsers.all_users) {
    return {
      all_users: false,
      automata_users: true,
      user_ids: allUsers
        .filter((user) => user.user_id !== value)
        .map((user) => user.user_id),
    };
  }

  return {
    all_users: false,
    automata_users: fromUsers.automata_users,
    user_ids: fromUsers.user_ids.includes(value)
      ? fromUsers.user_ids.filter((id) => id !== value)
      : [...fromUsers.user_ids, value],
  };
};
